/**
 * The quote's Terms & Conditions bullets.
 *
 * These used to be six hardcoded `<li>`s in the print template, copied from the shipped
 * quote OS-Q-47191888269. They were right for the common case and wrong for every quote
 * that needed one more line or one fewer — a rep with a customer who negotiated different
 * payment terms had no way to say so, and would either send a quote stating something
 * untrue or rebuild it by hand outside the tool.
 *
 * They are per-quote only. Nothing is persisted, so editing one here cannot change what
 * the next rep's quote says — the defaults below are the single source of the standard
 * wording, and a quote that needs the standard wording gets it by not touching anything.
 */

export interface QuoteTerm {
  /** Stable key for React and for row-level test ids. Never printed. */
  id: string;
  text: string;
}

export const TERMS_URL = 'https://www.optisigns.com/terms-of-use';

/**
 * The standard six, verbatim from the shipped quote. Order is deliberate: commercial terms
 * first, the legal catch-all last.
 */
export const DEFAULT_TERM_TEXTS: string[] = [
  'The annual license plan includes a 10% discount',
  'All payments by check will be assessed a $25 USD check processing fee',
  'Subject to taxes where applicable',
  'We ship out hardware after payment has been received',
  'All fees for all services are not Refundable (fully or partially)',
  `You agree with all Terms stated at ${TERMS_URL}`,
];

let seq = 0;
export function newTerm(text = ''): QuoteTerm {
  seq += 1;
  return { id: `term-${seq}`, text };
}

export function defaultTerms(): QuoteTerm[] {
  return DEFAULT_TERM_TEXTS.map((t) => newTerm(t));
}

/**
 * Split a bullet into text and URL runs so a link stays clickable after the line becomes
 * editable.
 *
 * The last default bullet carries the terms-of-use URL, which was an `<a>` in the template.
 * Now that the text is a plain string an operator can retype, the anchor has to be derived
 * from the text rather than hardcoded beside it — otherwise editing that bullet at all
 * would silently turn the link into flat text.
 *
 * Deliberately narrow: http/https only, and trailing sentence punctuation is left out of
 * the href so "see https://x.com." does not link to a 404.
 */
export function linkifyTerm(text: string): Array<{ text: string; href?: string }> {
  const parts: Array<{ text: string; href?: string }> = [];
  const re = /https?:\/\/[^\s]+/g;
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text)) !== null) {
    let url = m[0];
    // Pull trailing punctuation back out of the link.
    const trailing = url.match(/[.,;:!?)\]]+$/);
    if (trailing) url = url.slice(0, -trailing[0].length);

    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ text: url, href: url });
    last = m.index + url.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts.length ? parts : [{ text }];
}

/** Bullets that will actually print — a blank line is dropped rather than printed empty. */
export function printableTerms(terms: QuoteTerm[]): QuoteTerm[] {
  return terms.filter((t) => t.text.trim().length > 0);
}
